import React, { useState, useEffect } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import 'react-phone-number-input/style.css';
import 'flag-icons/css/flag-icons.min.css';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

const ContactInfo = ({ formData, handleChange }) => {
  // Get all countries from libphonenumber-js
  const allCountries = getCountries().map(country => ({
    code: country,
    name: new Intl.DisplayNames(['en'], { type: 'region' }).of(country),
    dialCode: getCountryCallingCode(country)
  }))
  .sort((a, b) => a.name.localeCompare(b.name));
  
  // Try to get saved country from localStorage or default to US
  const getSavedCountry = () => {
    const savedCountryCode = localStorage.getItem('selectedCountryCode');
    if (savedCountryCode) {
      const found = allCountries.find(c => c.code === savedCountryCode);
      if (found) return found;
    }
    return allCountries.find(c => c.code === 'US') || allCountries[0];
  };
  
  // Initialize with saved country or US
  const [selectedCountry, setSelectedCountry] = useState(getSavedCountry());
  
  // Internal state to track formatted display values
  const [displayValues, setDisplayValues] = useState({
    'bio.phone_number': formatForDisplay(formData.patient.bio.phone_number || ''),
    'bio.home_phone_number': formatForDisplay(formData.patient.bio.home_phone_number || ''),
    'bio.cell_phone_number': formatForDisplay(formData.patient.bio.cell_phone_number || ''),
    'bio.work_phone_number': formatForDisplay(formData.patient.bio.work_phone_number || ''),
    'bio.custom_contact_number': formatForDisplay(formData.patient.bio.custom_contact_number || '')
  });

  // Format number for display: (XXX) XXX-XXXX
  function formatForDisplay(cleanNumber) {
    // Remove all non-digit characters to get clean number
    const digits = cleanNumber.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digits.substring(0, 10);
    
    // Format for display
    if (limitedDigits.length > 0) {
      if (limitedDigits.length <= 3) {
        return `(${limitedDigits}`;
      } else if (limitedDigits.length <= 6) {
        return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
      } else {
        return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
      }
    }
    return '';
  }

  // Extract clean digits only from formatted number
  function getCleanNumber(formattedNumber) {
    return formattedNumber.replace(/\D/g, '');
  }

  // Handle phone input changes
  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    
    // Get clean digits
    const cleanNumber = getCleanNumber(value);
    
    // Format for display
    const formattedForDisplay = formatForDisplay(cleanNumber);
    
    // Update display value
    setDisplayValues({
      ...displayValues,
      [name]: formattedForDisplay
    });
    
    // Pass the clean number to parent's handleChange
    const syntheticEvent = {
      target: {
        name: name,
        value: cleanNumber // Only the digits, no country code
      }
    };
    
    handleChange(syntheticEvent);
  };
  
  // Handle country selection and save to localStorage
  const handleCountryChange = (e) => {
    const country = allCountries.find(c => c.code === e.target.value);
    setSelectedCountry(country);
    
    // Save to localStorage
    localStorage.setItem('selectedCountryCode', country.code);
  };
  
  // Create country dropdown items with flags
  const renderCountryOptions = () => {
    return allCountries.map(country => (
      <option key={country.code} value={country.code}>
        {country.name} (+{country.dialCode})
      </option>
    ));
  };

  // Flag element for the selected country
  const FlagElement = () => (
    <span className={`fi fi-${selectedCountry.code.toLowerCase()}`} style={{ marginRight: '8px' }}></span>
  );

  // Update the display values when formData changes
  useEffect(() => {
    setDisplayValues({
      'bio.phone_number': formatForDisplay(formData.patient.bio.phone_number || ''),
      'bio.home_phone_number': formatForDisplay(formData.patient.bio.home_phone_number || ''),
      'bio.cell_phone_number': formatForDisplay(formData.patient.bio.cell_phone_number || ''),
      'bio.work_phone_number': formatForDisplay(formData.patient.bio.work_phone_number || ''),
      'bio.custom_contact_number': formatForDisplay(formData.patient.bio.custom_contact_number || '')
    });
  }, [formData]);

  return (
    <div>
      <h3 className="h5 mb-3">Contact Information</h3>
      
      <Form.Group controlId="country" className="mb-3">
        <Form.Label>Country</Form.Label>
        <Form.Select 
          name="country"
          value={selectedCountry.code}
          onChange={handleCountryChange}
        >
          {renderCountryOptions()}
        </Form.Select>
      </Form.Group>
      
      <Form.Group controlId="phone" className="mb-3">
        <Form.Label>Primary Phone Number<span className="text-danger">*</span></Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <FlagElement /> +{selectedCountry.dialCode}
          </InputGroup.Text>
          <Form.Control
            type="tel"
            name="bio.phone_number"
            value={displayValues['bio.phone_number']}
            onChange={handlePhoneChange}
            required
            placeholder="(123) 456-7890"
          />
        </InputGroup>
      </Form.Group>
      
      <Form.Group controlId="homePhone" className="mb-3">
        <Form.Label>Home Phone Number</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <FlagElement /> +{selectedCountry.dialCode}
          </InputGroup.Text>
          <Form.Control
            type="tel"
            name="bio.home_phone_number"
            value={displayValues['bio.home_phone_number']}
            onChange={handlePhoneChange}
            placeholder="(123) 456-7890"
          />
        </InputGroup>
      </Form.Group>
      
      <Form.Group controlId="cellPhone" className="mb-3">
        <Form.Label>Cell Phone Number</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <FlagElement /> +{selectedCountry.dialCode}
          </InputGroup.Text>
          <Form.Control
            type="tel"
            name="bio.cell_phone_number"
            value={displayValues['bio.cell_phone_number']}
            onChange={handlePhoneChange}
            placeholder="(123) 456-7890"
          />
        </InputGroup>
      </Form.Group>
      
      <Form.Group controlId="workPhone" className="mb-3">
        <Form.Label>Work Phone Number</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <FlagElement /> +{selectedCountry.dialCode}
          </InputGroup.Text>
          <Form.Control
            type="tel"
            name="bio.work_phone_number"
            value={displayValues['bio.work_phone_number']}
            onChange={handlePhoneChange}
            placeholder="(123) 456-7890"
          />
        </InputGroup>
      </Form.Group>
      
      <Form.Group controlId="customContact" className="mb-3">
        <Form.Label>Alternative Contact Number</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <FlagElement /> +{selectedCountry.dialCode}
          </InputGroup.Text>
          <Form.Control
            type="tel"
            name="bio.custom_contact_number"
            value={displayValues['bio.custom_contact_number']}
            onChange={handlePhoneChange}
            placeholder="(123) 456-7890"
          />
        </InputGroup>
      </Form.Group>
    </div>
  );
};

export default ContactInfo;